import { ref, watchEffect, type Ref, isRef } from 'vue'
import { ApiError } from '@/lib/api'

interface UseApiReturn<T> {
  data: Ref<T | null>
  error: Ref<ApiError | null>
  loading: Ref<boolean>
  refresh: () => Promise<void>
}

export function useApi<T>(
  fetcher: () => Promise<T>,
  options?: { immediate?: boolean },
): UseApiReturn<T> {
  const data = ref<T | null>(null) as Ref<T | null>
  const error = ref<ApiError | null>(null)
  const loading = ref(false)

  const immediate = options?.immediate ?? true

  async function refresh() {
    loading.value = true
    error.value = null
    try {
      data.value = await fetcher()
    } catch (e) {
      if (e instanceof ApiError) {
        error.value = e
      } else {
        error.value = new ApiError(0, (e as Error).message)
      }
    } finally {
      loading.value = false
    }
  }

  if (immediate) {
    refresh()
  }

  return { data, error, loading, refresh }
}

/**
 * Reactive version that re-fetches when dependencies change.
 * Pass a reactive getter that returns the fetcher.
 */
export function useReactiveApi<T>(
  fetcherGetter: () => (() => Promise<T>) | null,
): UseApiReturn<T> {
  const data = ref<T | null>(null) as Ref<T | null>
  const error = ref<ApiError | null>(null)
  const loading = ref(false)

  let currentFetcher: (() => Promise<T>) | null = null

  async function refresh() {
    if (!currentFetcher) return
    loading.value = true
    error.value = null
    try {
      data.value = await currentFetcher()
    } catch (e) {
      if (e instanceof ApiError) {
        error.value = e
      } else {
        error.value = new ApiError(0, (e as Error).message)
      }
    } finally {
      loading.value = false
    }
  }

  watchEffect(() => {
    const fetcher = fetcherGetter()
    currentFetcher = fetcher
    if (fetcher) {
      refresh()
    }
  })

  return { data, error, loading, refresh }
}

// Convenience composables for common API calls
import { api } from '@/lib/api'
import type {
  Project,
  ProjectTree,
  FlagWithEnvironments,
  Environment,
  Segment,
  SyncStatus,
  Schedule,
  WebhookEndpoint,
  APIKey,
  OrgAPIKey,
  ConfigVersion,
  CloudConnection,
  Promotion,
} from '@/lib/types'

export function useProjects() {
  return useApi<Project[]>(() => api.listProjects())
}

export function useProjectTree() {
  return useApi<ProjectTree[]>(() => api.listProjectTree())
}

export function useProject(slug: Ref<string> | string) {
  return useReactiveApi<Project>(() => {
    const s = isRef(slug) ? slug.value : slug
    if (!s) return null
    return () => api.getProject(s)
  })
}

export function useFlags(project: Ref<string> | string) {
  return useReactiveApi<FlagWithEnvironments[]>(() => {
    const p = isRef(project) ? project.value : project
    if (!p) return null
    return () => api.listFlags(p)
  })
}

export function useFlag(project: Ref<string> | string, key: Ref<string> | string) {
  return useReactiveApi<FlagWithEnvironments>(() => {
    const p = isRef(project) ? project.value : project
    const k = isRef(key) ? key.value : key
    if (!p || !k) return null
    return () => api.getFlag(p, k)
  })
}

export function useEnvironments(project: Ref<string> | string) {
  return useReactiveApi<Environment[]>(() => {
    const p = isRef(project) ? project.value : project
    if (!p) return null
    return () => api.listEnvironments(p)
  })
}

export function useCloudConnections(project: Ref<string> | string, envName: Ref<string> | string) {
  return useReactiveApi<CloudConnection[]>(() => {
    const p = isRef(project) ? project.value : project
    const e = isRef(envName) ? envName.value : envName
    if (!p || !e) return null
    return () => api.listCloudConnections(p, e)
  })
}

export function useSegments(project: Ref<string> | string) {
  return useReactiveApi<Segment[]>(() => {
    const p = isRef(project) ? project.value : project
    if (!p) return null
    return () => api.listSegments(p)
  })
}

export function useSyncStatus(project: Ref<string> | string) {
  return useReactiveApi<SyncStatus[]>(() => {
    const p = isRef(project) ? project.value : project
    if (!p) return null
    return () => api.getSyncStatus(p)
  })
}

export function useSchedules(project: Ref<string> | string) {
  return useReactiveApi<Schedule[]>(() => {
    const p = isRef(project) ? project.value : project
    if (!p) return null
    return () => api.listSchedules(p)
  })
}

export function useWebhooks(project: Ref<string> | string) {
  return useReactiveApi<WebhookEndpoint[]>(() => {
    const p = isRef(project) ? project.value : project
    if (!p) return null
    return () => api.listWebhooks(p)
  })
}

export function useOrg() {
  return useApi<import('@/lib/types').OrgDetails>(() => api.getOrg())
}

export function useOrgApiKeys() {
  return useApi<OrgAPIKey[]>(() => api.listOrgApiKeys())
}

export function useApiKeys(project: Ref<string> | string) {
  return useReactiveApi<APIKey[]>(() => {
    const p = isRef(project) ? project.value : project
    if (!p) return null
    return () => api.listApiKeys(p)
  })
}

export function usePromotions(project: Ref<string> | string) {
  return useReactiveApi<Promotion[]>(() => {
    const p = isRef(project) ? project.value : project
    if (!p) return null
    return () => api.listPromotions(p)
  })
}

export function useVersions(project: Ref<string> | string, env: Ref<string> | string) {
  return useReactiveApi<ConfigVersion[]>(() => {
    const p = isRef(project) ? project.value : project
    const e = isRef(env) ? env.value : env
    if (!p || !e) return null
    return () => api.listVersions(p, e)
  })
}

export function usePushHistory(project: Ref<string> | string) {
  return useReactiveApi<import('@/lib/types').PushHistoryEntry[]>(() => {
    const p = isRef(project) ? project.value : project
    if (!p) return null
    return () => api.listPushHistory(p)
  })
}
